/**
 * @fileoverview The Active Client details modal (GET /sales/clients/{tenantId}).
 * @description One client in full: organisation, address, contacts (masked as the backend sent
 * them), the HR Admin block and who owns it. Every action button comes from the details'
 * `allowedActions` and never from the caller's role: Invite HR Admin, Edit, Archive, Restore (the
 * Resend control sits in the HR Admin block). A client that no longer exists (`404 / 4125`) closes
 * the modal and reloads the grid; a Tenant Management outage shows the backend's message with a
 * Retry. The parent mounts it only while it's open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useClientDetails } from '@/features/sales/hooks/useClientDetails'
import { useResendClientHrAdminInvite } from '@/features/sales/hooks/useResendClientHrAdminInvite'
import { hasClientAction } from '@/features/sales/lib/sales-clients.utils'
import { ApiException } from '@/shared/api/api.exception'
import Badge from '@/shared/components/atoms/Badge'
import Button from '@/shared/components/atoms/Button'
import Modal from '@/shared/components/molecules/Modal'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import ClientDetailPanels from './ClientDetailPanels'
import ClientHrAdminSection from './ClientHrAdminSection'
import SalesRequestError from './SalesRequestError'

import type { ClientDetails, HrAdminInviteResent } from '@/features/sales/types/sales-client.types'

/** @description A dialog the details modal can hand over to. */
export type ClientDialogKind = 'invite' | 'edit' | 'archive' | 'restore'

interface ClientDetailModalProps {
  tenantId: string
  bankName: string
  onClose: () => void
  onAction: (kind: ClientDialogKind) => void
  /** Called after Resend succeeds, with the bank's name and what the backend answered. */
  onResent: (bankName: string, result: HrAdminInviteResent) => void
  /** Called when the client no longer exists, so the page can reload the grid. */
  onGone: () => void
}

const ACTION_BUTTONS: { action: Parameters<typeof hasClientAction>[1]; kind: ClientDialogKind }[] =
  [
    { action: 'INVITE_HR_ADMIN', kind: 'invite' },
    { action: 'EDIT', kind: 'edit' },
    { action: 'DEACTIVATE', kind: 'archive' },
    { action: 'RESTORE', kind: 'restore' },
  ]

/**
 * @description The modal's action buttons: one per action the details allow.
 * @param {{ client: ClientDetails; onAction: (kind: ClientDialogKind) => void }} props - The client.
 * @param {ClientDetails} props.client - The client in full.
 * @param {(kind: ClientDialogKind) => void} props.onAction - Opens the action's dialog.
 */
function ClientActions({
  client,
  onAction,
}: {
  client: ClientDetails
  onAction: (kind: ClientDialogKind) => void
}) {
  const { t } = useTranslation()
  const buttons = ACTION_BUTTONS.filter(({ action }) => hasClientAction(client, action))
  if (buttons.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2.5" data-testid="client-detail-actions">
      {buttons.map(({ kind }) => (
        <div key={kind} className="w-fit">
          <Button
            type="button"
            size="md"
            variant={kind === 'archive' ? 'secondary' : 'primary'}
            onClick={() => onAction(kind)}
            data-testid={`client-${kind}-button`}
          >
            {t(`sales.activeClients.actions.${kind}`)}
          </Button>
        </div>
      ))}
    </div>
  )
}

/**
 * @component
 * @param {ClientDetailModalProps} props - The client and the handlers.
 * @param {string} props.tenantId - The client to show.
 * @param {string} props.bankName - The bank's name (the modal's subtitle while it loads).
 * @param {() => void} props.onClose - Called when the modal is dismissed.
 * @param {(kind: ClientDialogKind) => void} props.onAction - Opens an action's dialog.
 * @param {(bankName: string, result: HrAdminInviteResent) => void} props.onResent - Called after
 * the invitation is resent.
 * @param {() => void} props.onGone - Called when the client no longer exists.
 */
function ClientDetailModal({
  tenantId,
  bankName,
  onClose,
  onAction,
  onResent,
  onGone,
}: ClientDetailModalProps) {
  const { t } = useTranslation()
  const details = useClientDetails(tenantId)
  const resend = useResendClientHrAdminInvite(
    /**
     * @description Reports the resent invitation to the page, even if the modal has unmounted.
     * @param {HrAdminInviteResent} result - The invitation's new counters.
     */
    (result) => onResent(bankName, result),
  )
  const close = useGuardedClose(onClose, resend.isPending)

  /**
   * @description Resends this client's HR Admin invitation.
   */
  const resendInvite = () => resend.mutate(tenantId)
  const gone = details.error instanceof ApiException && details.error.status === 404
  const client = details.data?.client

  useEffect(() => {
    if (gone) onGone()
  }, [gone, onGone])

  return (
    <Modal
      open
      onClose={close}
      data-testid="client-detail-modal"
      title={client?.bankName ?? bankName}
      subtitle={
        client && (
          <Badge variant={client.status === 'ACTIVE' ? 'success' : 'neutral'} dot>
            {t(`sales.activeClients.status.${client.status}`)}
          </Badge>
        )
      }
      size="lg"
      footer={client && <ClientActions client={client} onAction={onAction} />}
    >
      {client ? (
        <div className="flex flex-col gap-4" data-testid="client-detail-body">
          <ClientDetailPanels client={client} />
          <ClientHrAdminSection
            client={client}
            onResend={resendInvite}
            isResending={resend.isPending}
            resendError={resend.isError ? resend.error : null}
          />
        </div>
      ) : details.isError && !gone ? (
        <div className="flex flex-col gap-3" data-testid="client-detail-error">
          <SalesRequestError error={details.error} />
          <div className="w-fit">
            <Button
              type="button"
              variant="secondary"
              size="md"
              isLoading={details.isFetching}
              onClick={() => void details.refetch()}
              data-testid="client-detail-retry-button"
            >
              {t('sales.activeClients.retry')}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-compact text-slate" data-testid="client-detail-loading">
          {t('sales.activeClients.loading')}
        </p>
      )}
    </Modal>
  )
}

export default ClientDetailModal
